import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { Invitation } from '../../types/domain';
import { userApiClient } from '../../infrastructure/userApi/client';

type CreateInvitationRequest = Parameters<
  typeof userApiClient.createInvitation
>[0];

type UseInvitationsData = {
  isLoading: boolean;
  invitations: Invitation[];
  createInvitation: (data: CreateInvitationRequest) => Promise<void>;
  deleteInvitation: (invitationId: string) => Promise<void>;
};

const EMPTY_DATA = [] as const satisfies Invitation[];

const useInvitationsData = (): UseInvitationsData => {
  const queryClient = useQueryClient();

  const { data, isFetching } = useQuery({
    queryKey: ['invitations'],
    queryFn: async (): Promise<Invitation[]> => {
      return userApiClient.getInvitations();
    },
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    meta: { dataType: 'others' },
  });

  const { mutateAsync: createMutation } = useMutation({
    mutationFn: async (data: CreateInvitationRequest): Promise<void> => {
      await userApiClient.createInvitation(data);
    },
    onSuccess: (): Promise<void> => {
      return queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });

  const { mutateAsync: deleteMutation } = useMutation({
    mutationFn: (invitationId: string): Promise<void> => {
      return userApiClient.deleteInvitation(invitationId);
    },
    onSuccess: (): Promise<void> => {
      return queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });

  return {
    isLoading: isFetching,
    invitations: data ?? EMPTY_DATA,
    createInvitation: createMutation,
    deleteInvitation: deleteMutation,
  };
};

export { useInvitationsData };
